<script setup>
import Layout from "@/FrontComponents/Layout.vue";
import { Link, useForm } from "@inertiajs/vue3";

const props = defineProps({
    latestProducts: {
        type: Array,
        default: () => []
    },
    totalProducts: {
        type: Number,
        default: 0
    },
    popularCategories: {
        type: Array,
        default: () => []
    }
});

const form = useForm({
    model_id: null,
    brand_id: null,
    search: '',
    category_id: null,
    order: 'asc',
});

function submitSearch() {
    if (form.search && form.search.trim() !== '') {
        form.get('/catalog', { preserveScroll: true });
    } else {
        form.get('/catalog');
    }
}

function quickSearchTag(tag) {
    form.search = tag;
    submitSearch();
}

function formatPrice(val) {
    if (!val) return '0';
    return Number(val).toLocaleString('uk-UA');
}

function getProductImage(product) {
    if (product.main_image?.img_url) {
        return product.main_image.img_url;
    }
    return 'https://images.unsplash.com/photo-1486006920555-c77dce18193b?auto=format&fit=crop&w=600&q=80';
}

// Popular car brands configuration
const popularCarBrands = [
    { name: 'BMW', badge: 'BMW', color: 'from-blue-600 to-sky-500' },
    { name: 'Mercedes-Benz', badge: 'MB', color: 'from-slate-700 to-slate-900' },
    { name: 'Audi', badge: 'AUDI', color: 'from-red-600 to-slate-900' },
    { name: 'Volkswagen', badge: 'VW', color: 'from-blue-700 to-blue-900' },
    { name: 'Toyota', badge: 'TOYOTA', color: 'from-red-500 to-red-700' },
    { name: 'Opel', badge: 'OPEL', color: 'from-amber-500 to-yellow-600' },
    { name: 'Ford', badge: 'FORD', color: 'from-blue-600 to-indigo-800' },
    { name: 'Renault', badge: 'RENAULT', color: 'from-yellow-500 to-amber-600' },
    { name: 'Skoda', badge: 'SKODA', color: 'from-emerald-600 to-green-800' },
    { name: 'Nissan', badge: 'NISSAN', color: 'from-slate-600 to-slate-800' },
    { name: 'Hyundai', badge: 'HYUNDAI', color: 'from-blue-800 to-slate-900' },
    { name: 'Kia', badge: 'KIA', color: 'from-red-700 to-slate-900' },
];

const categoryVisuals = [
    '/images/categories/engine.png',
    '/images/categories/alternator.png',
    '/images/categories/turbo.png',
    '/images/categories/fuel_system.png',
    '/images/categories/transmission.png',
    '/images/categories/brakes.png',
    '/images/categories/suspension.png',
    '/images/categories/headlights.png',
];

function getCategoryBg(category, index) {
    const catName = typeof category === 'object' && category?.name ? category.name.toLowerCase() : '';

    if (catName.includes('двиг') || catName.includes('мотор') || catName.includes('блок')) {
        return '/images/categories/engine.png';
    }
    if (catName.includes('генер') || catName.includes('старт') || catName.includes('електр') || catName.includes('электр')) {
        return '/images/categories/alternator.png';
    }
    if (catName.includes('турб') || catName.includes('наддув') || catName.includes('інтеркул') || catName.includes('интеркул')) {
        return '/images/categories/turbo.png';
    }
    if (catName.includes('палив') || catName.includes('топлив') || catName.includes('форсунк') || catName.includes('тнвд')) {
        return '/images/categories/fuel_system.png';
    }
    if (catName.includes('трансм') || catName.includes('кпп') || catName.includes('короб') || catName.includes('зчеп') || catName.includes('сцепл') || catName.includes('редуктор')) {
        return '/images/categories/transmission.png';
    }
    if (catName.includes('гальм') || catName.includes('тормоз') || catName.includes('колодк') || catName.includes('диск')) {
        return '/images/categories/brakes.png';
    }
    if (catName.includes('підвіс') || catName.includes('подвес') || catName.includes('амортиз') || catName.includes('важіль') || catName.includes('рычаг') || catName.includes('рульов') || catName.includes('ходов')) {
        return '/images/categories/suspension.png';
    }
    if (catName.includes('оптик') || catName.includes('фар') || catName.includes('кузов') || catName.includes('світл') || catName.includes('свет') || catName.includes('бампер')) {
        return '/images/categories/headlights.png';
    }

    return categoryVisuals[index % categoryVisuals.length];
}
</script>

<template>
    <Layout title="OlAvtoStore — Швидкий пошук та каталог автозапчастин">
        <main class="bg-slate-50">
            <!-- Hero Banner Section -->
            <section class="relative bg-slate-950 text-white overflow-hidden py-16 sm:py-24 border-b border-slate-800">
                <!-- Glowing Ambient Gradients -->
                <div class="absolute inset-0 opacity-30 pointer-events-none">
                    <div class="absolute -top-32 -left-32 w-[500px] h-[500px] bg-teal-500 rounded-full blur-[120px]"></div>
                    <div class="absolute top-1/2 -right-32 w-[500px] h-[500px] bg-emerald-500 rounded-full blur-[120px]"></div>
                    <div class="absolute -bottom-32 left-1/3 w-[400px] h-[400px] bg-sky-600 rounded-full blur-[120px]"></div>
                </div>

                <div class="container mx-auto px-4 sm:px-6 relative z-10 text-center max-w-4xl">
                    <!-- Top Counter Pill -->
                    <div class="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-slate-900/80 border border-teal-500/30 text-teal-400 text-xs font-bold uppercase tracking-wider mb-8 shadow-lg backdrop-blur-md">
                        <span class="flex h-2 w-2 relative">
                            <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
                            <span class="relative inline-flex rounded-full h-2 w-2 bg-teal-500"></span>
                        </span>
                        <span>⚡ Понад {{ (props.totalProducts || 100000).toLocaleString('uk-UA') }} запчастин у наявності</span>
                    </div>

                    <h1 class="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white mb-6 leading-tight">
                        Оригінальні автозапчастини та якісні аналоги для вашого авто
                    </h1>

                    <p class="text-slate-300 text-base sm:text-lg mb-10 max-w-2xl mx-auto leading-relaxed font-medium">
                        Швидкий пошук за каталожним номером, назвою або кодом деталі. Гарантія сумісності та швидка доставка по Україні.
                    </p>

                    <!-- Search Box Card -->
                    <div class="max-w-3xl mx-auto bg-slate-900/90 backdrop-blur-2xl p-4 sm:p-5 rounded-3xl border border-slate-700/80 shadow-2xl">
                        <form @submit.prevent="submitSearch" class="flex flex-col sm:flex-row gap-3">
                            <div class="relative flex-grow">
                                <input 
                                    v-model="form.search" 
                                    type="text" 
                                    placeholder="Введіть артикул, OE код або назву деталі (напр. 059130089)..." 
                                    class="w-full px-5 py-4 bg-slate-800/90 rounded-2xl text-white placeholder-slate-400 text-sm font-medium border border-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all shadow-inner"
                                >
                            </div>
                            <button 
                                type="submit" 
                                class="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-400 hover:to-emerald-500 text-white font-extrabold rounded-2xl shadow-lg shadow-teal-500/25 transition-all transform hover:-translate-y-0.5 active:translate-y-0 text-sm flex items-center justify-center gap-2.5 shrink-0"
                            >
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
                                <span>Знайти запчастину</span>
                            </button>
                        </form>

                        <!-- Quick Filter Tags -->
                        <div class="mt-4 pt-3 border-t border-slate-800/80 flex flex-wrap items-center justify-center gap-2 text-xs">
                            <span class="text-slate-400 font-semibold mr-1">Популярні запити:</span>
                            <button @click="quickSearchTag('BMW')" class="px-3 py-1 rounded-full bg-slate-800 hover:bg-teal-500/20 hover:text-teal-400 text-slate-300 font-medium transition-colors border border-slate-700">BMW</button>
                            <button @click="quickSearchTag('Турбіна')" class="px-3 py-1 rounded-full bg-slate-800 hover:bg-teal-500/20 hover:text-teal-400 text-slate-300 font-medium transition-colors border border-slate-700">Турбіна</button>
                            <button @click="quickSearchTag('Форсунка')" class="px-3 py-1 rounded-full bg-slate-800 hover:bg-teal-500/20 hover:text-teal-400 text-slate-300 font-medium transition-colors border border-slate-700">Форсунка</button>
                            <button @click="quickSearchTag('Генератор')" class="px-3 py-1 rounded-full bg-slate-800 hover:bg-teal-500/20 hover:text-teal-400 text-slate-300 font-medium transition-colors border border-slate-700">Генератор</button>
                            <button @click="quickSearchTag('Audi')" class="px-3 py-1 rounded-full bg-slate-800 hover:bg-teal-500/20 hover:text-teal-400 text-slate-300 font-medium transition-colors border border-slate-700">Audi</button>
                        </div>
                    </div>

                    <!-- Trust Stats Row -->
                    <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-12 max-w-3xl mx-auto">
                        <div class="flex items-center justify-center gap-3 p-3.5 rounded-2xl bg-slate-900/60 border border-slate-800">
                            <div class="w-10 h-10 rounded-xl bg-teal-500/10 text-teal-400 flex items-center justify-center shrink-0">
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>
                            </div>
                            <div class="text-left">
                                <h4 class="text-xs font-bold text-white">Перевірка за VIN</h4>
                                <p class="text-[11px] text-slate-400">100% точність підбору</p>
                            </div>
                        </div>

                        <div class="flex items-center justify-center gap-3 p-3.5 rounded-2xl bg-slate-900/60 border border-slate-800">
                            <div class="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center shrink-0">
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
                            </div>
                            <div class="text-left">
                                <h4 class="text-xs font-bold text-white">Швидка відправка</h4>
                                <p class="text-[11px] text-slate-400">Доставка 1-3 дні</p>
                            </div>
                        </div>

                        <div class="flex items-center justify-center gap-3 p-3.5 rounded-2xl bg-slate-900/60 border border-slate-800">
                            <div class="w-10 h-10 rounded-xl bg-sky-500/10 text-sky-400 flex items-center justify-center shrink-0">
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
                            </div>
                            <div class="text-left">
                                <h4 class="text-xs font-bold text-white">Гарантія 14 днів</h4>
                                <p class="text-[11px] text-slate-400">Повернення та обмін</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Popular Car Brands Gallery -->
            <section class="bg-white border-b border-slate-200 py-12 sm:py-16">
                <div class="container mx-auto px-4 sm:px-6">
                    <div class="flex flex-col sm:flex-row sm:items-end justify-between mb-8 gap-4">
                        <div>
                            <span class="text-teal-600 text-xs font-bold uppercase tracking-wider">Марки авто</span>
                            <h2 class="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-1">Швидкий підбір за маркою автомобіля</h2>
                        </div>
                        <Link href="/catalog" class="text-xs font-bold text-teal-600 hover:text-teal-700 flex items-center gap-1.5 group shrink-0">
                            <span>Всі марки у каталозі</span>
                            <svg class="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/></svg>
                        </Link>
                    </div>

                    <!-- Brands Grid -->
                    <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                        <Link 
                            v-for="brand in popularCarBrands" 
                            :key="brand.name" 
                            :href="`/catalog?search=${encodeURIComponent(brand.name)}`" 
                            class="group bg-slate-50 rounded-2xl p-5 border border-slate-200/80 hover:bg-white hover:border-teal-500/40 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 flex flex-col items-center justify-center text-center"
                        >
                            <div :class="['w-12 h-12 rounded-2xl bg-gradient-to-br flex items-center justify-center text-white font-black text-xs shadow-md mb-3 group-hover:scale-110 transition-transform', brand.color]">
                                {{ brand.badge }}
                            </div>
                            <span class="text-sm font-bold text-slate-800 group-hover:text-teal-600 transition-colors">
                                {{ brand.name }}
                            </span>
                            <span class="text-[10px] text-slate-400 font-semibold mt-1 flex items-center gap-0.5 group-hover:text-teal-500">
                                Запчастини &rarr;
                            </span>
                        </Link>
                    </div>
                </div>
            </section>

            <!-- Featured / Latest Products Showcase Section -->
            <section class="container mx-auto px-4 sm:px-6 py-16 sm:py-20">
                <div class="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
                    <div>
                        <span class="inline-flex items-center gap-1.5 text-emerald-600 text-xs font-bold uppercase tracking-wider">
                            <span class="w-2 h-2 rounded-full bg-emerald-500"></span>
                            Оновлення каталогу
                        </span>
                        <h2 class="text-3xl font-extrabold text-slate-900 mt-1">Нові надходження та популярні запчастини</h2>
                    </div>
                    <Link href="/catalog" class="text-sm font-bold text-teal-600 hover:text-teal-700 flex items-center gap-1 group">
                        <span>Переглянути весь асортимент</span>
                        <svg class="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/></svg>
                    </Link>
                </div>

                <!-- Products Grid -->
                <div v-if="props.latestProducts && props.latestProducts.length > 0" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div 
                        v-for="product in props.latestProducts" 
                        :key="product.id"
                        class="group bg-white rounded-3xl border border-slate-200/80 overflow-hidden hover:shadow-2xl hover:border-teal-500/30 transition-all duration-300 flex flex-col"
                    >
                        <div class="relative aspect-4/3 overflow-hidden bg-slate-100">
                            <img 
                                :src="getProductImage(product)" 
                                :alt="product.name"
                                class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            >
                            <div class="absolute top-3 left-3 flex flex-wrap gap-1.5">
                                <span class="px-2.5 py-1 bg-emerald-500/90 backdrop-blur-md text-white text-[10px] font-bold rounded-lg uppercase tracking-wider shadow-xs">
                                    В наявності
                                </span>
                                <span v-if="product.brand?.name" class="px-2.5 py-1 bg-slate-900/80 backdrop-blur-md text-white text-[10px] font-bold rounded-lg uppercase tracking-wider shadow-xs">
                                    {{ product.brand.name }}
                                </span>
                            </div>
                        </div>

                        <div class="p-5 flex flex-col flex-grow">
                            <span v-if="product.category?.name" class="text-[11px] font-bold text-teal-600 uppercase tracking-wider mb-1">
                                {{ product.category.name }}
                            </span>
                            <h3 class="text-sm font-bold text-slate-900 line-clamp-2 mb-3 leading-snug group-hover:text-teal-600 transition-colors">
                                {{ product.name }}
                            </h3>

                            <div class="mt-auto pt-4 border-t border-slate-100 flex items-center justify-between">
                                <div class="flex flex-col">
                                    <span class="text-[10px] text-slate-400 font-semibold uppercase">Ціна</span>
                                    <span class="text-lg font-black text-slate-900">
                                        {{ formatPrice(product.convert_price_uah) }} <span class="text-xs font-bold text-teal-600">₴</span>
                                    </span>
                                </div>
                                <Link 
                                    :href="`/view/${product.id}`"
                                    class="px-4 py-2.5 rounded-xl bg-slate-900 group-hover:bg-teal-600 text-white font-bold text-xs transition-colors shadow-xs flex items-center gap-1"
                                >
                                    <span>Деталі</span>
                                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Fallback Categories Grid if products array empty -->
                <div v-else class="text-center py-12 bg-white rounded-3xl border border-slate-200/80">
                    <p class="text-slate-500 font-semibold mb-4">Ознайомтесь з каталогом запчастин у наявності</p>
                    <Link href="/catalog" class="px-6 py-3 bg-teal-600 text-white font-bold text-xs rounded-xl shadow-md">
                        Перейти до каталогу
                    </Link>
                </div>
            </section>

            <!-- Popular Categories Grid фіва
            <section id="catalog" class="bg-slate-100/70 border-y border-slate-200 py-16 sm:py-20">
                <div class="container mx-auto px-4 sm:px-6">
                    <div class="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
                        <div>
                            <span class="text-teal-600 text-xs font-bold uppercase tracking-wider">Категорії</span>
                            <h2 class="text-3xl font-extrabold text-slate-900 mt-1">Популярні розділи автозапчастин</h2>
                        </div>
                        <Link href="/catalog" class="text-sm font-semibold text-teal-600 hover:text-teal-700 flex items-center gap-1 group">
                            <span>Переглянути всі категорії</span>
                            <svg class="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/></svg>
                        </Link>
                    </div>

                    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        <Link 
                            v-for="(category, index) in (props.popularCategories && props.popularCategories.length > 0 ? props.popularCategories : [
                                { id: 162, name: 'Двигуни та деталі', products_count: 1450 },
                                { id: 120, name: 'Коробки передач & КПП', products_count: 530 },
                                { id: 447, name: 'Генератори & Стартери', products_count: 820 },
                                { id: 189, name: 'Турбіни & Наддув', products_count: 640 }
                            ])"
                            :key="category.id"
                            :href="`/catalog?category=${category.id}`" 
                            class="group bg-white rounded-3xl border border-slate-200/80 shadow-xs hover:shadow-2xl hover:border-teal-500/40 overflow-hidden transition-all duration-300 transform hover:-translate-y-1"
                        >
                            <div class="aspect-4/3 overflow-hidden bg-slate-100 relative">
                                <img 
                                    :src="getCategoryBg(category, index)" 
                                    :alt="category.name" 
                                    class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                >
                                <div class="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-900/30 to-transparent"></div>
                                <div class="absolute bottom-4 left-4 right-4">
                                    <span v-if="category.products_count" class="inline-block px-2.5 py-0.5 bg-teal-500 text-white text-[10px] font-bold rounded-full mb-2">
                                        {{ category.products_count }} запчастин
                                    </span>
                                    <h3 class="font-extrabold text-lg text-white leading-snug">
                                        {{ category.name }}
                                    </h3>
                                    <span class="text-xs text-teal-300 font-semibold mt-1 flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                                        Перейти в розділ &rarr;
                                    </span>
                                </div>
                            </div>
                        </Link>
                    </div>
                </div>
            </section>-->

            <!-- 4-Step Interactive Order Workflow -->
            <section class="container mx-auto px-4 sm:px-6 py-16 sm:py-20">
                <div class="text-center max-w-2xl mx-auto mb-14">
                    <span class="text-teal-600 text-xs font-bold uppercase tracking-wider">Простий процес</span>
                    <h2 class="text-3xl font-extrabold text-slate-900 mt-1">Як замовити запчастину у 4 прості кроки</h2>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <div class="bg-white p-6 rounded-3xl border border-slate-200/80 relative shadow-xs hover:shadow-lg transition-shadow">
                        <div class="w-12 h-12 rounded-2xl bg-teal-500/10 text-teal-600 font-black text-xl flex items-center justify-center mb-5">
                            01
                        </div>
                        <h3 class="text-lg font-bold text-slate-900 mb-2">Пошук та вибір</h3>
                        <p class="text-slate-600 text-xs leading-relaxed">
                            Знайдіть деталь у каталозі за назвою, кодом або залишіть заявку на підбір.
                        </p>
                    </div>

                    <div class="bg-white p-6 rounded-3xl border border-slate-200/80 relative shadow-xs hover:shadow-lg transition-shadow">
                        <div class="w-12 h-12 rounded-2xl bg-emerald-500/10 text-emerald-600 font-black text-xl flex items-center justify-center mb-5">
                            02
                        </div>
                        <h3 class="text-lg font-bold text-slate-900 mb-2">Перевірка сумісності</h3>
                        <p class="text-slate-600 text-xs leading-relaxed">
                            Менеджер безкоштовно перевірить сумісність запчастини за VIN-кодом вашого авто.
                        </p>
                    </div>

                    <div class="bg-white p-6 rounded-3xl border border-slate-200/80 relative shadow-xs hover:shadow-lg transition-shadow">
                        <div class="w-12 h-12 rounded-2xl bg-sky-500/10 text-sky-600 font-black text-xl flex items-center justify-center mb-5">
                            03
                        </div>
                        <h3 class="text-lg font-bold text-slate-900 mb-2">Фотофіксація</h3>
                        <p class="text-slate-600 text-xs leading-relaxed">
                            Надаємо реальні деталізовані фото та огляд запчастини перед упакуванням.
                        </p>
                    </div>

                    <div class="bg-white p-6 rounded-3xl border border-slate-200/80 relative shadow-xs hover:shadow-lg transition-shadow">
                        <div class="w-12 h-12 rounded-2xl bg-indigo-500/10 text-indigo-600 font-black text-xl flex items-center justify-center mb-5">
                            04
                        </div>
                        <h3 class="text-lg font-bold text-slate-900 mb-2">Швидка доставка</h3>
                        <p class="text-slate-600 text-xs leading-relaxed">
                            Відправляємо замовлення у день оформлення службою Нова Пошта по всій Україні.
                        </p>
                    </div>
                </div>
            </section>

            <!-- Exclusive Service & Guarantees Section -->
            <section id="about" class="bg-white border-y border-slate-200 py-16 sm:py-20">
                <div class="container mx-auto px-4 sm:px-6">
                    <div class="text-center max-w-2xl mx-auto mb-16">
                        <span class="text-teal-600 text-xs font-bold uppercase tracking-wider">Переваги OlAvtoStore</span>
                        <h2 class="text-3xl font-extrabold text-slate-900 mt-1">Чому автовласники та СТО обирають нас?</h2>
                    </div>

                    <div class="grid md:grid-cols-3 gap-8">
                        <div class="p-8 rounded-3xl bg-slate-50 border border-slate-200/80 hover:border-teal-500/30 transition-all text-center group">
                            <div class="w-14 h-14 rounded-2xl bg-teal-500/10 text-teal-600 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                                <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>
                            </div>
                            <h3 class="text-xl font-bold text-slate-900 mb-2">Понад 100 000 найменувань</h3>
                            <p class="text-slate-600 text-sm leading-relaxed">Величезний вибір оригінальних запчастин та перевірених ліцензійних аналогів в наявності.</p>
                        </div>

                        <div class="p-8 rounded-3xl bg-slate-50 border border-slate-200/80 hover:border-teal-500/30 transition-all text-center group">
                            <div class="w-14 h-14 rounded-2xl bg-teal-500/10 text-teal-600 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                                <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
                            </div>
                            <h3 class="text-xl font-bold text-slate-900 mb-2">Оперативна доставка</h3>
                            <p class="text-slate-600 text-sm leading-relaxed">Відправка у день замовлення Новою Поштою з можливістю післяплати при отриманні.</p>
                        </div>

                        <div class="p-8 rounded-3xl bg-slate-50 border border-slate-200/80 hover:border-teal-500/30 transition-all text-center group">
                            <div class="w-14 h-14 rounded-2xl bg-teal-500/10 text-teal-600 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                                <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                            </div>
                            <h3 class="text-xl font-bold text-slate-900 mb-2">Офіційна гарантія</h3>
                            <p class="text-slate-600 text-sm leading-relaxed">Чесна оцінка стану деталей, гарантійний термін на перевірку та встановлення.</p>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Quick Assistance Call-To-Action Banner -->
            <section class="container mx-auto px-4 sm:px-6 py-16">
                <div class="bg-gradient-to-br from-slate-900 via-slate-950 to-teal-950 rounded-3xl p-8 sm:p-12 text-white relative overflow-hidden border border-slate-800 shadow-2xl">
                    <div class="absolute -right-16 -bottom-16 w-80 h-80 bg-teal-500/10 rounded-full blur-3xl pointer-events-none"></div>

                    <div class="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8">
                        <div class="max-w-2xl text-center lg:text-left">
                            <span class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/20 text-teal-300 text-xs font-bold uppercase tracking-wider mb-4">
                                💬 Консультація фахівця
                            </span>
                            <h2 class="text-3xl sm:text-4xl font-black mb-4">
                                Не знайшли потрібну деталь?
                            </h2>
                            <p class="text-slate-300 text-sm sm:text-base leading-relaxed">
                                Зателефонуйте нашому менеджеру або напишіть у месенджер. Ми безкоштовно підберемо потрібну запчастину за VIN-кодом вашого автомобіля!
                            </p>
                        </div>

                        <div class="flex flex-col sm:flex-row items-center gap-4 shrink-0">
                            <a 
                                href="tel:+380501356035" 
                                class="px-8 py-4 bg-teal-500 hover:bg-teal-400 text-white font-extrabold rounded-2xl shadow-lg shadow-teal-500/30 transition-all transform hover:-translate-y-0.5 text-sm flex items-center gap-2.5"
                            >
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                                <span>+38 (050) 135-60-35</span>
                            </a>
                            <a 
                                href="https://t.me/+380501356035" 
                                target="_blank"
                                rel="noopener noreferrer"
                                class="px-6 py-4 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-2xl border border-slate-700 transition-all text-sm flex items-center gap-2"
                            >
                                <span>Telegram</span>
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    </Layout>
</template>

<style scoped>
</style>
